import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface CategoryHeaderGradientSelectorProps {
  primaryColor: string;
  secondaryColor: string;
  onPrimaryColorChange: (color: string) => void;
  onSecondaryColorChange: (color: string) => void;
  globalPrimaryColor?: string;
  globalSecondaryColor?: string;
}

/**
 * CategoryHeaderGradientSelector - Configure gradient colors for category headers
 */
const CategoryHeaderGradientSelector: React.FC<CategoryHeaderGradientSelectorProps> = ({
  primaryColor,
  secondaryColor,
  onPrimaryColorChange,
  onSecondaryColorChange,
  globalPrimaryColor = '#c5b068',
  globalSecondaryColor = '#a59456'
}) => {
  const gradientPresets = [
    { name: 'Blue to Purple', primary: '#3b82f6', secondary: '#8b5cf6' },
    { name: 'Green to Blue', primary: '#10b981', secondary: '#3b82f6' },
    { name: 'Orange to Red', primary: '#f97316', secondary: '#ef4444' },
    { name: 'Pink to Rose', primary: '#ec4899', secondary: '#f43f5e' },
    { name: 'Gold to Orange', primary: '#f59e0b', secondary: '#f97316' },
    { name: 'Teal to Green', primary: '#14b8a6', secondary: '#10b981' }
  ];

  const handlePresetClick = (preset: typeof gradientPresets[0]) => {
    onPrimaryColorChange(preset.primary);
    onSecondaryColorChange(preset.secondary);
  };

  const handleSwapColors = () => {
    const temp = primaryColor;
    onPrimaryColorChange(secondaryColor);
    onSecondaryColorChange(temp);
  };

  const handleResetToGlobal = () => {
    onPrimaryColorChange(globalPrimaryColor);
    onSecondaryColorChange(globalSecondaryColor);
  };

  const isUsingGlobalColors = primaryColor === globalPrimaryColor && secondaryColor === globalSecondaryColor;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Gradient Colours</Label>
        <div 
          className="w-20 h-6 rounded border border-gray-300"
          style={{ 
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` 
          }}
        />
      </div>
      
      <div className="space-y-4">
        {/* Color Controls */}
        <div className="grid grid-cols-2 gap-3">
          {/* Primary Color */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-600">Primary Colour</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                >
                  <div 
                    className="w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: primaryColor }}
                  />
                  {primaryColor}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64">
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Pick primary colour</Label>
                  <Input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => onPrimaryColorChange(e.target.value)}
                    className="w-full h-10"
                  />
                  <Input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => onPrimaryColorChange(e.target.value)}
                    placeholder="#ffffff"
                    className="font-mono text-sm"
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Secondary Color */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-600">Secondary Colour</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                >
                  <div 
                    className="w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: secondaryColor }}
                  />
                  {secondaryColor}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64">
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Pick secondary colour</Label>
                  <Input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => onSecondaryColorChange(e.target.value)}
                    className="w-full h-10"
                  />
                  <Input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => onSecondaryColorChange(e.target.value)}
                    placeholder="#ffffff"
                    className="font-mono text-sm"
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSwapColors}
            className="w-full"
          >
            ↔ Swap Colours
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetToGlobal}
            disabled={isUsingGlobalColors}
            className="w-full"
          >
            ↺ Reset to Global
          </Button>
        </div>

        {/* Gradient Presets */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-600">Quick Presets</Label>
          <div className="grid grid-cols-2 gap-2">
            {gradientPresets.map((preset) => (
              <Button
                key={preset.name}
                variant="outline"
                size="sm"
                onClick={() => handlePresetClick(preset)}
                className="justify-start gap-2 h-auto p-2"
              >
                <div 
                  className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                  style={{ 
                    background: `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)` 
                  }}
                />
                <span className="text-xs text-left">{preset.name}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="text-xs text-gray-500">
        Configure the primary and secondary colours for the gradient text effect
      </div>
    </div>
  );
};

export default CategoryHeaderGradientSelector;